/**
 * Custom React Hook for Patient Diagnosis Management
 * Handles state, auto-save, and API interactions
 *
 * @author CareX Frontend Team
 */

import { useState, useEffect, useCallback } from 'react';
import patientDiagnosisService from '../services/patientDiagnosisService';
import { formatToBackendDate } from '../utils/dateUtils';
import {
  PatientDiagnosisDTO,
  ExamVitalsDTO,
  AmbulatoryStatusDTO,
  DiagnosesTreatmentsDTO,
  PhysicalHealthAssessmentDTO,
  MentalConditionAssessmentDTO,
  CapacityMedicationAssessmentDTO,
  HospicePhysicianInfoDTO,
} from '../types/diagnosis.types';

interface UsePatientDiagnosisProps {
  residentId: string | null;
  diagnosisId?: string | null;
}

interface UsePatientDiagnosisReturn {
  // State
  diagnosis: PatientDiagnosisDTO | null;
  loading: boolean;
  saving: boolean;
  error: string | null;

  // Actions
  saveDraft: () => Promise<void>;
  updateExamVitals: (data: ExamVitalsDTO) => Promise<void>;
  updateAmbulatoryStatus: (data: AmbulatoryStatusDTO) => Promise<void>;
  updateDiagnosesTreatments: (data: DiagnosesTreatmentsDTO) => Promise<void>;
  updatePhysicalHealthAssessment: (data: PhysicalHealthAssessmentDTO) => Promise<void>;
  updateMentalConditionAssessment: (data: MentalConditionAssessmentDTO) => Promise<void>;
  updateCapacityMedicationAssessment: (data: CapacityMedicationAssessmentDTO) => Promise<void>;
  updateHospicePhysicianInfo: (data: HospicePhysicianInfoDTO) => Promise<void>;
  submitDiagnosis: () => Promise<void>;
  refreshDiagnosis: () => Promise<void>;

  // Helpers
  isEditable: boolean;
  completionPercentage: number;
}

/**
 * Custom hook for managing patient diagnosis
 * Provides auto-save, state management, and error handling
 */
export const usePatientDiagnosis = ({
  residentId,
  diagnosisId,
}: UsePatientDiagnosisProps): UsePatientDiagnosisReturn => {
  const [diagnosis, setDiagnosis] = useState<PatientDiagnosisDTO | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize or fetch diagnosis
  useEffect(() => {
    const fetchDiagnosis = async () => {
      try {
        setLoading(true);
        setError(null);

        if (diagnosisId) {
          const fetchedDiagnosis = await patientDiagnosisService.getDiagnosisById(diagnosisId);
          setDiagnosis(fetchedDiagnosis);
          return;
        }

        if (residentId) {
          // Get existing draft diagnosis if it exists
          const fetchedDiagnosis = await patientDiagnosisService.getLatestActive(residentId);
          setDiagnosis(fetchedDiagnosis);
          return;
        }

        setDiagnosis(null);
      } catch (err: any) {
        console.error('Error fetching diagnosis:', err);
        setError(err.response?.data?.message || 'Failed to load diagnosis');
      } finally {
        setLoading(false);
      }
    };

    fetchDiagnosis();
  }, [residentId, diagnosisId]);

  // Refresh diagnosis from server
  const refreshDiagnosis = useCallback(async () => {
    if (!diagnosis?.id) return;

    try {
      const updated = await patientDiagnosisService.getDiagnosisById(diagnosis.id);
      setDiagnosis(updated);
    } catch (err: any) {
      console.error('Error refreshing diagnosis:', err);
      setError(err.response?.data?.message || 'Failed to refresh diagnosis');
    }
  }, [diagnosis]);

  // Save draft (create new if doesn't exist)
  const saveDraft = useCallback(async () => {
    if (!residentId) return;

    try {
      setSaving(true);
      setError(null);

      if (!diagnosis?.id) {
        // Create new draft
        // const newDiagnosis = await patientDiagnosisService.createDiagnosis({
        //   residentId,
        //   diagnosisDate: new Date().toISOString().split('T')[0],
        // });
        // setDiagnosis(newDiagnosis);
      }
    } catch (err: any) {
      console.error('Error saving draft:', err);
      setError(err.response?.data?.message || 'Failed to save draft');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [residentId, diagnosis]);

  // Helper to ensure we have an ID before patching
  const getOrSyncId = async (currentDiagnosis: PatientDiagnosisDTO | null): Promise<string> => {
    if (currentDiagnosis?.id) return currentDiagnosis.id;
    if (!residentId) throw new Error("Resident ID is missing");

    try {
      // const created = await patientDiagnosisService.createDiagnosis({
      //   residentId,
      //   diagnosisDate: formatToBackendDate(new Date()) || "",
      // });
      // setDiagnosis(created);
      // return created.id;
      return "temp-id-for-now";
    } catch (err: any) {
      if (err.response?.data?.message?.includes("already exists")) {
        const latest = await patientDiagnosisService.getLatestActive(residentId);
        if (latest) {
          setDiagnosis(latest);
          return latest.id;
        }
      }
      throw err;
    }
  };

  // Tab update methods
  const updateExamVitals = useCallback(
    async (data: ExamVitalsDTO) => {
      try {
        setSaving(true);
        setError(null);

        // const id = await getOrSyncId(diagnosis);
        // const updated = await patientDiagnosisService.updateExamVitals(id, data);
        // setDiagnosis(updated);
      } catch (err: any) {
        console.error('Error updating exam vitals:', err);
        setError(err.response?.data?.message || 'Failed to save exam vitals');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [diagnosis, residentId]
  );

  const updateAmbulatoryStatus = useCallback(
    async (data: AmbulatoryStatusDTO) => {
      try {
        setSaving(true);
        setError(null);

        // const id = await getOrSyncId(diagnosis);
        // const updated = await patientDiagnosisService.updateAmbulatoryStatus(id, data);
        // setDiagnosis(updated);
      } catch (err: any) {
        console.error('Error updating ambulatory status:', err);
        setError(err.response?.data?.message || 'Failed to save ambulatory status');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [diagnosis, residentId]
  );

  const updateDiagnosesTreatments = useCallback(
    async (data: DiagnosesTreatmentsDTO) => {
      try {
        setSaving(true);
        setError(null);

        // const id = await getOrSyncId(diagnosis);
        // const updated = await patientDiagnosisService.updateDiagnosesTreatments(id, data);
        // setDiagnosis(updated);
      } catch (err: any) {
        console.error('Error updating diagnoses/treatments:', err);
        setError(err.response?.data?.message || 'Failed to save diagnoses and treatments');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [diagnosis, residentId]
  );

  const updatePhysicalHealthAssessment = useCallback(
    async (data: PhysicalHealthAssessmentDTO) => {
      try {
        setSaving(true);
        setError(null);

        // const id = await getOrSyncId(diagnosis);
        // const updated = await patientDiagnosisService.updatePhysicalHealthAssessment(id, data);
        // setDiagnosis(updated);
      } catch (err: any) {
        console.error('Error updating physical health:', err);
        setError(err.response?.data?.message || 'Failed to save physical health assessment');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [diagnosis, residentId]
  );

  const updateMentalConditionAssessment = useCallback(
    async (data: MentalConditionAssessmentDTO) => {
      try {
        setSaving(true);
        setError(null);

        // const id = await getOrSyncId(diagnosis);
        // const updated = await patientDiagnosisService.updateMentalConditionAssessment(id, data);
        // setDiagnosis(updated);
      } catch (err: any) {
        console.error('Error updating mental condition:', err);
        setError(err.response?.data?.message || 'Failed to save mental condition assessment');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [diagnosis, residentId]
  );

  const updateCapacityMedicationAssessment = useCallback(
    async (data: CapacityMedicationAssessmentDTO) => {
      try {
        setSaving(true);
        setError(null);

        // const id = await getOrSyncId(diagnosis);
        // const updated = await patientDiagnosisService.updateCapacityMedicationAssessment(id, data);
        // setDiagnosis(updated);
      } catch (err: any) {
        console.error('Error updating capacity/medication:', err);
        setError(err.response?.data?.message || 'Failed to save capacity and medication assessment');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [diagnosis, residentId]
  );

  const updateHospicePhysicianInfo = useCallback(
    async (data: HospicePhysicianInfoDTO) => {
      try {
        setSaving(true);
        setError(null);

        // const id = await getOrSyncId(diagnosis);
        // const updated = await patientDiagnosisService.updateHospicePhysicianInfo(id, data);
        // setDiagnosis(updated);
      } catch (err: any) {
        console.error('Error updating hospice/physician info:', err);
        setError(err.response?.data?.message || 'Failed to save hospice and physician information');
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [diagnosis, residentId]
  );

  const submitDiagnosis = useCallback(async () => {
    try {
      setSaving(true);
      setError(null);

      // 1. Ensure we have an ID on the server
      // const id = await getOrSyncId(diagnosis);

      // 2. The sections are staged in local state. 
      // All previous update... calls (triggered by Next button) have already patched their respective sections.
      // However, for extra robustness during the final submit, we ensure the current status is transitioned.
      // const updated = await patientDiagnosisService.submitDiagnosis(id);
      // setDiagnosis(updated);
    } catch (err: any) {
      console.error('Error submitting diagnosis:', err);
      setError(err.response?.data?.message || 'Failed to submit diagnosis');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [diagnosis, residentId]);

  // Computed properties
  const isEditable = diagnosis ? patientDiagnosisService.isEditable(diagnosis) : true;
  const completionPercentage = diagnosis?.completionPercentage || 0;

  return {
    // State
    diagnosis,
    loading,
    saving,
    error,

    // Actions
    saveDraft,
    updateExamVitals,
    updateAmbulatoryStatus,
    updateDiagnosesTreatments,
    updatePhysicalHealthAssessment,
    updateMentalConditionAssessment,
    updateCapacityMedicationAssessment,
    updateHospicePhysicianInfo,
    submitDiagnosis,
    refreshDiagnosis,

    // Helpers
    isEditable,
    completionPercentage,
  };
};
